import { useState } from "react";
import { useAdminRefresh } from "@/lib/booking";

export const inputCls = "mt-2 w-full rounded-xl border border-ink/15 bg-ocean/60 px-4 py-2.5 text-sm text-ink placeholder:text-dim/50 focus:border-neon-cyan focus:outline-none";
export const smallBtn = "rounded-full border border-ink/25 px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-dim transition-colors hover:border-neon-cyan hover:text-neon-cyan disabled:opacity-40";

export function useAction() {
    const refresh = useAdminRefresh();
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const run = async (fn, ok = "Salvo!") => {
        setBusy(true);
        setErr("");
        setMsg("");
        try {
            const r = await fn();
            if (r?.error) throw r.error;
            setMsg(ok);
            refresh();
            setTimeout(() => setMsg(""), 2500);
            return true;
        } catch (e) {
            setErr(e.message || String(e));
            return false;
        } finally {
            setBusy(false);
        }
    };
    return { run, busy, msg, err };
}

export function Select({ label, value, onChange, options, testid }) {
    return (
        <label className="block">
            <span className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">{label}</span>
            <select value={value ?? ""} data-testid={testid} onChange={(e) => onChange(e.target.value)} className={inputCls}>
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </label>
    );
}

export function MultiCheck({ label, options, value, onChange, testid }) {
    const list = value || [];
    const toggle = (v) => onChange(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
    return (
        <fieldset data-testid={testid}>
            <legend className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">{label}</legend>
            <div className="mt-2 flex flex-wrap gap-2">
                {options.map(([v, l]) => (
                    <button type="button" key={v} onClick={() => toggle(v)}
                        className={`rounded-full border px-3 py-1.5 text-xs ${list.includes(v) ? "border-neon-cyan text-neon-cyan" : "border-ink/20 text-dim"}`}>{l}</button>
                ))}
            </div>
        </fieldset>
    );
}

export function PageTitle({ title, children }) {
    return (
        <div>
            <h1 className="font-display text-2xl font-black uppercase sm:text-3xl">{title}</h1>
            {children && <p className="mt-2 text-sm text-dim">{children}</p>}
        </div>
    );
}

export function SubTitle({ children }) {
    return <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">{children}</p>;
}

export function Card({ children, className = "" }) {
    return (
        <div
            className={`rounded-3xl border border-ink/10 bg-deep p-6 sm:p-7 ${className}`}
        >
            {children}
        </div>
    );
}

export function Field({
    label,
    value,
    onChange,
    type = "text",
    placeholder,
    testid,
}) {
    return (
        <label className="block">
            <span className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                {label}
            </span>
            <input
                type={type}
                value={value ?? ""}
                placeholder={placeholder}
                data-testid={testid}
                onChange={(e) => onChange(e.target.value)}
                className="mt-2 w-full rounded-xl border border-ink/15 bg-ocean/60 px-4 py-2.5 text-sm text-ink placeholder:text-dim/50 focus:border-neon-cyan focus:outline-none"
            />
        </label>
    );
}

export function Area({ label, value, onChange, rows = 3, testid }) {
    return (
        <label className="block">
            <span className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-neon-cyan">
                {label}
            </span>
            <textarea
                rows={rows}
                value={value ?? ""}
                data-testid={testid}
                onChange={(e) => onChange(e.target.value)}
                className="mt-2 w-full rounded-xl border border-ink/15 bg-ocean/60 px-4 py-2.5 text-sm text-ink placeholder:text-dim/50 focus:border-neon-cyan focus:outline-none"
            />
        </label>
    );
}

export function Toggle({ checked, onChange, label, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={() => onChange(!checked)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors ${
                checked
                    ? "border-neon-teal/50 bg-neon-teal/10 text-neon-teal"
                    : "border-ink/20 bg-ocean/60 text-dim"
            }`}
        >
            <span
                className={`h-2 w-2 rounded-full ${
                    checked ? "bg-neon-teal" : "bg-dim/50"
                }`}
            />
            {label}
        </button>
    );
}

export function SaveBtn({ onClick, saving, testid, children = "Salvar" }) {
    return (
        <button
            type="button"
            data-testid={testid}
            disabled={saving}
            onClick={onClick}
            className="inline-flex items-center gap-2 rounded-full bg-neon-cyan px-6 py-2.5 font-display text-[10px] font-extrabold uppercase tracking-widest text-ocean transition-transform duration-300 hover:scale-105 disabled:opacity-50"
        >
            {saving ? "Salvando..." : children}
        </button>
    );
}

export function DelBtn({ onClick, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className="inline-flex items-center rounded-full border border-neon-coral/40 px-5 py-2.5 font-display text-[10px] font-extrabold uppercase tracking-widest text-neon-coral transition-colors hover:bg-neon-coral/10"
        >
            Excluir
        </button>
    );
}

export function Ok({ msg }) {
    if (!msg) return null;
    return (
        <p
            data-testid="admin-save-ok"
            className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-neon-teal"
        >
            {msg}
        </p>
    );
}

export function Err({ msg }) {
    if (!msg) return null;
    return (
        <p
            data-testid="admin-save-err"
            className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-neon-coral"
        >
            {msg}
        </p>
    );
}

export function Notice({ tone = "amber", children, testid }) {
    const cls =
        tone === "amber"
            ? "border-neon-sun/40 bg-neon-sun/10 text-neon-sun"
            : tone === "red"
              ? "border-neon-coral/40 bg-neon-coral/10 text-neon-coral"
              : "border-neon-teal/40 bg-neon-teal/10 text-neon-teal";
    return (
        <div
            data-testid={testid}
            className={`rounded-2xl border px-5 py-4 text-sm leading-relaxed ${cls}`}
        >
            {children}
        </div>
    );
}
